import { HttpStateData, Model } from '@/clients/http'

type ErrorBody = { message?: string }

export interface ListResult<T> {
  page?: number
  length?: number
  count: number
  result: T[]
}

export type ListQuery = Record<string, string | number | boolean | undefined>

/**
 * The calls a plain backoffice resource has (list / create / update / soft delete). The POS
 * resources (suppliers, units, price books, tiers, branches, terminals) share one route shape, so their models are
 * built here instead of repeating the Brand model four times over.
 */
export interface ResourceApi<T> {
  getList: (query?: ListQuery) => Promise<HttpStateData<ListResult<T>, ErrorBody>>
  create: (body: Record<string, unknown>) => Promise<HttpStateData<{ result: T }, ErrorBody>>
  update: (id: string, body: Record<string, unknown>) => Promise<HttpStateData<{ result: T }, ErrorBody>>
  remove: (id: string) => Promise<HttpStateData<unknown, ErrorBody>>
}

export const createResourceApi = <T>(url: string, key: string): ResourceApi<T> => ({
  getList: (query) =>
    Model.createHttpRequest<ListResult<T>, ErrorBody>({
      correlationKey: `GET_${key}_LIST`,
      payload: { method: 'get', url: Model.createEndpoint({ url, query }), useBearerToken: true },
    }),
  create: (body) =>
    Model.createHttpRequest<{ result: T }, ErrorBody>({
      correlationKey: `CREATE_${key}`,
      payload: { method: 'post', url: Model.createEndpoint({ url }), body, useBearerToken: true },
    }),
  update: (id, body) =>
    Model.createHttpRequest<{ result: T }, ErrorBody>({
      correlationKey: `UPDATE_${key}`,
      payload: { method: 'put', url: Model.createEndpoint({ url: `${url}/:id`, params: { id } }), body, useBearerToken: true },
    }),
  remove: (id) =>
    Model.createHttpRequest<unknown, ErrorBody>({
      correlationKey: `DELETE_${key}`,
      payload: { method: 'delete', url: Model.createEndpoint({ url: `${url}/:id`, params: { id } }), useBearerToken: true },
    }),
})
